/* eslint-disable no-nested-ternary */
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import ReactImageMagnify from 'react-image-magnify';
import SimpleBar from 'simplebar-react';

/** Components */
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Skeleton from '@mui/material/Skeleton';
import { useTheme } from '@mui/material/styles';
import CustomImage from 'components/CustomImage';
import BabyGlideCarousel from 'components/GlideCarousel/BabyGlideCarousel';
import WishListButton from 'components/WishListButton';

/** Hooks */
import useAuth from 'hooks/useAuth';
import useResponsive from 'hooks/useResponsive';
import useWishlist from 'hooks/useWishlist';

// Utils & Consts
import { IMAGE_RESIZING_CDN_BASE_URL } from 'constants';
import { LIST_ITEM_FALLBACK } from 'constants/images-defaults';
import { GTMProductWishlistEvents } from 'utils/gtm';

const productDetailImageGalleryDefaultPropSx = {};

const ProductDetailImageGallery = ({
  sx = productDetailImageGalleryDefaultPropSx,
  images,
  loading = false,
  product = PropTypes.shape({
    WP_id: '112344554',
    productName: 'Product Name',
    wholesaler: {
      name: 'Wholesaler Name',
    },
    produtSlug: '',
    productCategories: [],
    productThumbnailUrl: {
      productThumbnailName: '',
      productThumbnailBaseUrl: '',
    },
  }),
  productWSP = 0,
  babyGlideRef,
}) => {
  const theme = useTheme();
  const carouselImageRef = useRef(null);
  const isMobile = useResponsive('down', 'sm');
  const isTablet = useResponsive('down', 'md');
  const { isAuthenticated, userId } = useAuth();
  const [carouselImageWidth, setCarouselWidth] = useState(600);
  const { wishlist, addToWishlist, removeFromWishlist } = useWishlist();

  const handleImageClick = (index) => {
    babyGlideRef.current.goToSlide(index);
  };

  // wishlist data & handlers
  const [inWishlist, setInWishlist] = useState(false);

  useEffect(() => {
    const productInWishlist = wishlist?.filter((x) => x.productId === product?.WP_id).pop();

    if (productInWishlist) {
      setInWishlist(true);
    } else {
      setInWishlist(false);
    }
  }, [wishlist, product?.WP_id]);

  const handleAddToWishlist = async () => {
    await addToWishlist([{ productId: product.WP_id }]);
    GTMProductWishlistEvents(
      product.productName,
      product.WP_id,
      productWSP,
      product.wholesaler?.name,
      `/product/${product.productSlug}`,
      `${product?.productThumbnailUrl?.productThumbnailBaseUrl}/${product?.productThumbnailUrl?.productThumbnailName}`,
      product?.productCategories,
      userId
    );
  };

  const handleRemoveFromWishlist = async () => {
    await removeFromWishlist({ productId: product.WP_id });
  };

  useEffect(() => {
    if (carouselImageRef?.current) {
      setCarouselWidth(carouselImageRef?.current.clientWidth);
    }
  }, []);

  return (
    <Box
      sx={{
        ...sx,
      }}
    >
      <Box sx={{ display: { xs: 'none', md: 'block' } }}>
        {loading ? (
          Array.from({ length: 6 }).map((_, idx) => (
            <Skeleton
              // eslint-disable-next-line react/no-array-index-key
              key={`skeleton-${idx}`}
              variant="rectangular"
              width={100}
              height={100}
              sx={{
                mb: 1,
                display: {
                  md: idx < 2 ? 'block' : 'none',
                  lg: idx < 4 ? 'block' : 'none',
                  xl: idx < 6 ? 'block' : 'none',
                },
              }}
            />
          ))
        ) : (
          <SimpleBar style={{ maxHeight: carouselImageWidth, width: '100px' }} autoHide={false}>
            {images.map((image, index) => (
              <Box sx={{ width: 100, height: 100, mb: 1 }} onClick={() => handleImageClick(index)} key={image.img}>
                <Link href={`#image-${index + 1}`}>
                  <CustomImage
                    width={100}
                    height={100}
                    src={image.img}
                    alt={image.title ? `${image.title}-${index + 1}` : `${product.productName}-${index}`}
                    fallbackSrc={LIST_ITEM_FALLBACK}
                  />
                </Link>
              </Box>
            ))}
          </SimpleBar>
        )}
      </Box>

      <Box
        sx={{
          position: 'relative',
          [theme.breakpoints.up('md')]: {
            width: 'calc(100% - 100px - 1rem)',
            '& > div': {
              height: loading ? undefined : carouselImageWidth,
            },
          },
        }}
        ref={carouselImageRef}
      >
        {loading ? (
          <Box sx={{ aspectRatio: '1' }}>
            <Skeleton variant="rectangular" width="100%" height="100%" />
          </Box>
        ) : images.length > 1 ? (
          <BabyGlideCarousel
            ref={babyGlideRef}
            config={{
              isMobile,
              swipeable: isMobile,
              hideBullets: !isTablet,
              bulletsLength: images?.length,
            }}
          >
            {images.map((image, index) => (
              <Box
                sx={{
                  position: 'relative',
                  [theme.breakpoints.down('md')]: {
                    width: 'calc(100vw - 2rem)',
                    height: 'calc(100vw - 2rem)',
                  },
                }}
                key={image.img}
              >
                {isAuthenticated && (
                  <Box
                    sx={{
                      position: 'absolute',
                    }}
                  >
                    <WishListButton
                      {...{ inWishlist, handleRemoveFromWishlist, handleAddToWishlist }}
                      sx={{
                        zIndex: 200,
                        top: 16,
                        left: 16,
                      }}
                    />
                  </Box>
                )}
                <ReactImageMagnify
                  smallImage={{
                    src: image.img.includes('/assets') ? image.img : `${IMAGE_RESIZING_CDN_BASE_URL}/${image.img}`,
                    alt: `${image.title}-${index + 1}`,
                    isFluidWidth: true,
                  }}
                  largeImage={{
                    width: 1800,
                    height: 1800,
                    src: image.img.includes('/assets') ? image.img : `${IMAGE_RESIZING_CDN_BASE_URL}/${image.img}`,
                  }}
                  enlargedImagePosition="over"
                  lensStyle={{
                    background: 'hsla(0, 0%, 100%, .3)',
                    border: '1px solid #ccc',
                  }}
                />
              </Box>
            ))}
          </BabyGlideCarousel>
        ) : (
          <ReactImageMagnify
            smallImage={{
              src: images[0].img.includes('/assets')
                ? images[0].img
                : `${IMAGE_RESIZING_CDN_BASE_URL}/${images[0].img}`,
              alt: `${images[0].title}-1`,
              isFluidWidth: true,
            }}
            largeImage={{
              width: 1800,
              height: 1800,
              src: images[0].img.includes('/assets')
                ? images[0].img
                : `${IMAGE_RESIZING_CDN_BASE_URL}/${images[0].img}`,
            }}
            enlargedImagePosition="over"
            lensStyle={{
              background: 'hsla(0, 0%, 100%, .3)',
              border: '1px solid #ccc',
            }}
          />
        )}
      </Box>
    </Box>
  );
};

ProductDetailImageGallery.propTypes = {
  product: PropTypes.shape({
    WP_id: PropTypes.string,
    productName: PropTypes.string,
    wholesaler: PropTypes.shape({
      name: PropTypes.string,
    }),
    productSlug: PropTypes.string,
    productCategories: PropTypes.arrayOf(PropTypes.string),
    productThumbnailUrl: PropTypes.shape({
      productThumbnailName: PropTypes.string,
      productThumbnailBaseUrl: PropTypes.string,
    }),
  }),
  productWSP: PropTypes.number,
  loading: PropTypes.bool,
  sx: PropTypes.shape({}),
  images: PropTypes.arrayOf(
    PropTypes.shape({
      cols: PropTypes.number,
      rows: PropTypes.number,
      img: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
    })
  ).isRequired,
  babyGlideRef: PropTypes.shape({
    current: PropTypes.shape({
      goToSlide: PropTypes.func,
    }),
  }).isRequired,
};

export default ProductDetailImageGallery;
